export class NumericUtils {
	/**
	 * Fonction permettant d'arrondir un nombre à la décimale demandée.
	 * @param nombre le nombre.
	 * @param fractionDigits le nombre de décimales qu'on veut conserver.
	 */
	static arrondiNDigits(nombre: number, fractionDigits = 3): number {
		return nombre ? parseFloat(nombre.toFixed(fractionDigits)) : nombre;
	}

	/**
	 * Vérifie si une variable est un numérique valide.
	 * La fonction prend en compte la locale française et une virgule sera
	 * considéré comme un séparateur de décimale.
	 *
	 * La fonction ne traite pas "10e2" comme une puissance.
	 *
	 * @param {*} value Variable à vérifier.
	 * @returns {boolean} True si la variable est un numérique, false sinon.
	 */
	static isNumeric(value: any): boolean {
		const numberRegex = /^-{0,1}(\d+([\.,]\d+|[\.,]|)|([\.,]\d+))$/;
		return value !== undefined && value !== null && numberRegex.test(value.toString().trim());
	}

	/**
	 * Permet d'adapter un nombre à la valeur attendue. Si le nombre est une chaîne alors on a un undefined sinon on ramène la valeur float.
	 * @param nombre le nombre à corriger
	 */
	static toNumber(nombre: any): number {
		if (nombre === null || nombre === undefined || !this.isNumeric(nombre)) {
			return undefined;
		}
		const str = nombre.toString().replace(',', '.');
		return parseFloat(str);
	}

	/**
	 * Convertit une chaîne de caractère en nombre numérique.
	 * La fonction prend en compte la locale française et une virgule sera
	 * considéré comme un séparateur de décimale.
	 *
	 * @param {*} value Valeur à convertir.
	 * @returns {Number} La valeur numérique.
	 */
	static toNumeric(value: any): number {
		return this.toNumericOrDefault(value, { defaultValue: 0 });
	}

	/**
	 * Convertit une chaîne de caractère en nombre numérique si cela est possible.
	 * La fonction prend en compte la locale française et une virgule sera considérée comme un séparateur de décimale.
	 * Si la valeur n'est pas interprétable comme numérique, on retourne la valeur par défaut voulue.
	 *
	 * @param value Valeur à convertir.
	 * @param opts Les options à spécifier pour la conversion
	 * * `defaultValue` La valeur par défaut à renvoyer dans le cas ou le nombre n'est pas un numérique.
	 * * `emptyStringValue` La valeur à renvoyer dans le cas ou la valeur est un string vide
	 * @returns {number|T} La valeur numérique si la valeur est numérique, la valeur par défaut sinon.
	 */
	static toNumericOrDefault<T>(value: T, opts?: { defaultValue?: T | number, emptyStringValue?: T | number }): T | number {
		const defaultValue = opts?.hasOwnProperty('defaultValue') ? opts.defaultValue : value;
		const emptyStringValue = opts?.hasOwnProperty('emptyStringValue') ? opts.emptyStringValue : value;

		let obj;
		if (typeof value === 'string') {
			obj = value.replace(',', '.');
			if (obj.trim() === '') {
				return emptyStringValue;
			}
		}
		else {
			obj = value;
		}

		if (this.isNumeric(obj)) {
			return Number(obj) || 0;
		}
		else {
			return defaultValue;
		}
	}

	/**
	 * Verifie que la valeur est null (ou undefined) ou 0.
	 *
	 * @param nombre la valeur à tester.
	 * @return True si value est null (ou undefined) ou 0, false sinon.
	 */
	static isNullOrZero(nombre: number): boolean {
		return nombre == null || nombre === 0;
	}

	/**
	 * Détermine si la valeur est un prix
	 * @param value la valeur à évaluer
	 */
	static isPrice(value: any): boolean {
		const priceRegex = /^(\d+([\.,]\d{1,2}|[\.,]|)|([\.,]\d{1,2}))$/;
		return (NumericUtils.isNumeric(value) && priceRegex.test(value.toString()));
	}
}
